import { FC, ReactNode, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAccountType } from "@/contexts/AccountTypeContext";

import * as Styled from "./styles";
import { renderStepContent, Steps } from "./utils";

type TQuestionFlowDialog = {
  open: boolean;
  onClose: () => void;
};

const SLIDE_DURATION = 450;

const isFinalStep = (step: Steps): boolean =>
  step >= Steps.FINAL_FREE_USE;

const getProgress = (step: Steps): number => {
  switch (step) {
    case Steps.START:
      return 0;
    case Steps.MUSIC_USE:
      return 10;
    case Steps.ADVERTISEMENT:
      return 22;
    case Steps.BROADCAST_NETWORK:
      return 33;
    case Steps.MUSIC_CONTENT:
      return 44;
    case Steps.LIVESTREAMS:
      return 55;
    case Steps.WILLING_TO_PAY:
      return 67;
    case Steps.GIVE_CREDIT:
      return 76;
    case Steps.FREQUENCY:
      return 85;
    case Steps.TRACK_COUNT:
      return 93;
    default:
      return 100;
  }
};

export const QuestionFlowDialog: FC<TQuestionFlowDialog> = ({ onClose, open }) => {
  const [activeStep, setActiveStep] = useState<Steps>(Steps.START);
  const [panelA, setPanelA] = useState<ReactNode>(null);
  const [panelB, setPanelB] = useState<ReactNode>(null);
  const [sliding, setSliding] = useState(false);
  const [offset, setOffset] = useState(0);
  const [progress, setProgress] = useState(0);
  const [viewportHeight, setViewportHeight] = useState<number | undefined>(undefined);

  const panelARef = useRef<HTMLDivElement>(null);
  const panelBRef = useRef<HTMLDivElement>(null);
  const isFirstStep = useRef(true);
  const maxHeightRef = useRef(0);
  const pendingSlideRef = useRef(false);
  const navigate = useNavigate();
  const { accountType } = useAccountType();
  // Locks interaction for the whole transition: blocks a second (fast) click
  // from starting an overlapping slide (which desynced the panel swap), and
  // disables pointer-events so the incoming option doesn't flash its hover
  // state as it remounts under the cursor. Set synchronously on a valid click,
  // cleared one frame after the slide finalizes.
  const lockedRef = useRef(false);
  const [locked, setLocked] = useState(false);

  // Initialize panel A with start content
  useEffect(() => {
    if (open) {
      const startContent = renderStepContent(
        Steps.START,
        (finalStep?: Steps) => {
          if (lockedRef.current) return;
          lockedRef.current = true;
          setLocked(true);
          setActiveStep(finalStep !== undefined ? finalStep : Steps.MUSIC_USE);
        },
        onClose,
        accountType,
      );
      setPanelA(startContent);
      setPanelB(null);
      setOffset(0);
      setSliding(false);
      setProgress(0);
      isFirstStep.current = true;
      maxHeightRef.current = 0;
      setViewportHeight(undefined);
    }
  }, [open, onClose, accountType]);

  // Handle step transitions (slide animation)
  useEffect(() => {
    if (!open) return;

    // Build new content with fresh callbacks for the new step
    const newContent = renderStepContent(
      activeStep,
      (finalStep?: Steps) => {
        if (lockedRef.current) return;
        lockedRef.current = true;
        setLocked(true);
        setActiveStep(finalStep !== undefined ? finalStep : ((activeStep + 1) as Steps));
      },
      onClose,
      accountType,
    );

    // On first render (START), just set panel A (no slider yet)
    if (isFirstStep.current) {
      setPanelA(newContent);
      isFirstStep.current = false;
      return;
    }

    setProgress(getProgress(activeStep));

    // First transition: switch from static to slider mode
    if (viewportHeight === undefined) {
      // Measure current start page height
      const startHeight = panelARef.current?.scrollHeight || 0;
      // Switch to slider: set panelA to current (start), panelB to new content
      const currentA = panelA;
      setPanelA(currentA);
      setPanelB(newContent);
      // Set initial height to start page height, then animate to max
      setViewportHeight(startHeight);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Measure new content and set max height
          if (panelBRef.current) {
            const h = panelBRef.current.scrollHeight;
            if (h > maxHeightRef.current) maxHeightRef.current = h;
          }
          if (panelARef.current) {
            const h = panelARef.current.scrollHeight;
            if (h > maxHeightRef.current) maxHeightRef.current = h;
          }
          setViewportHeight(maxHeightRef.current);
          setSliding(true);
          setOffset(-50);

          setTimeout(() => {
            setSliding(false);
            setPanelA(newContent);
            setPanelB(null);
            setOffset(0);
            // Unlock a frame later, after the remount settles, so the option
            // under the cursor doesn't flash its hover state.
            requestAnimationFrame(() => {
              lockedRef.current = false;
              setLocked(false);
            });
          }, SLIDE_DURATION + 10);
        });
      });
      return;
    }

    // Subsequent transitions: set panelB and flag pending slide
    setPanelB(newContent);
    pendingSlideRef.current = true;
  }, [activeStep, open, onClose, accountType]);

  // Execute slide after panelB content has rendered
  useEffect(() => {
    if (!panelB || !pendingSlideRef.current) return;
    pendingSlideRef.current = false;

    // Measure incoming content height
    const incomingHeight = panelBRef.current?.scrollHeight || 0;

    // Set target height before starting slide so both animate together.
    // Finals + a restart (back to START) size to their own content; questions
    // size to the tallest-so-far to avoid jumpiness between steps.
    if (isFinalStep(activeStep) || activeStep === Steps.START) {
      setViewportHeight(incomingHeight);
    } else {
      if (incomingHeight > maxHeightRef.current) maxHeightRef.current = incomingHeight;
      setViewportHeight(maxHeightRef.current);
    }

    requestAnimationFrame(() => {
      setSliding(true);
      setOffset(-50);

      setTimeout(() => {
        setSliding(false);
        setPanelA(panelB);
        setPanelB(null);
        setOffset(0);
        requestAnimationFrame(() => {
          lockedRef.current = false;
          setLocked(false);
        });
      }, SLIDE_DURATION + 10);
    });
  }, [panelB, activeStep]);

  // Reset on close
  useEffect(() => {
    if (!open) {
      const timer = setTimeout(() => {
        setActiveStep(Steps.START);
        setPanelA(null);
        setPanelB(null);
        setOffset(0);
        setSliding(false);
        setProgress(0);
        lockedRef.current = false;
        setLocked(false);
        isFirstStep.current = true;
        maxHeightRef.current = 0;
        setViewportHeight(undefined);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const cleanUrl = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete("test");
    const cleanPath = url.pathname + url.search + url.hash;
    navigate(cleanPath, { replace: true });
  };

  const close = () => {
    onClose();
    cleanUrl();
  };

  return (
    <>
      <Styled.Overlay $open={open} />
      <Styled.ModalContainer $open={open}>
        <Styled.CloseBtn onClick={close} aria-label="Close" style={{ opacity: open ? 1 : 0 }}>
          <svg viewBox="0 0 24 24">
            <line x1="6" y1="6" x2="18" y2="18" />
            <line x1="18" y1="6" x2="6" y2="18" />
          </svg>
        </Styled.CloseBtn>
        <Styled.ModalInner $open={open}>
          <Styled.ProgressBarWrapper>
            <Styled.ProgressBar>
              <Styled.ProgressFill $progress={progress} />
            </Styled.ProgressBar>
          </Styled.ProgressBarWrapper>
          {viewportHeight === undefined ? (
            <Styled.SlidePanel ref={panelARef} sx={{ width: "100%" }}>{panelA}</Styled.SlidePanel>
          ) : (
            <Styled.SliderViewport $locked sx={{ height: viewportHeight }}>
              <Styled.SliderTrack $sliding={sliding} $offset={offset} $locked={locked}>
                <Styled.SlidePanel ref={panelARef}>{panelA}</Styled.SlidePanel>
                <Styled.SlidePanel ref={panelBRef}>{panelB}</Styled.SlidePanel>
              </Styled.SliderTrack>
            </Styled.SliderViewport>
          )}
        </Styled.ModalInner>
      </Styled.ModalContainer>
    </>
  );
};
